import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  Typography,
  FormLabel,
} from "@mui/material";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import "./AddServiceForm.css";
import { baseUrl } from "../../../../../network/constants/constants";

const AddServiceForm = ({ open, onClose, categoryList, onSubmit, service }) => {
  const [formState, setFormState] = useState({
    service: "",
    category: "",
    description: "",
    open_time: {
      hour: "00",
      minute: "00",
    },
    close_time: {
      hour: "00",
      minute: "00",
    },
    mobile_number: "",
    thumb: null,
    features: [{ feature_name: "", color: "" }],
    price_chart: {
      price_chart: "",
      details: "",
      text: "",
    },
    terms_and_condition: "",
  });

  useEffect(() => {
    if (service) {
      setFormState({
        service: service?.service,
        category: service?.category,
        description: service?.description,
        open_time: service?.open_time,
        close_time: service?.close_time,
        mobile_number: service?.mobile_number,
        thumb: service?.thumb,
        features: service?.features,
        price_chart: service?.price_chart,
        terms_and_condition: service?.terms_and_condition
      });
    }
    return () => {
      setFormState({
        service: "",
        category: "",
        description: "",
        open_time: "",
        close_time: "",
        mobile_number: "",
        thumb: null,
        features: [{ feature_name: "", color: "" }],
        price_chart: {
          price_chart: "",
          details: "",
          text: "",
        },
        terms_and_condition: "",
      });
    };
  }, [service]);

  const [errors, setErrors] = useState({});

  const validationForm = () => {
    const newErrors = {};

    if (!formState.service) newErrors.service = "Service is required";
    if (!formState.category) newErrors.category = "Please select a category";
    if (!formState.description)
      newErrors.description = "Description is required";

    if (!formState.mobile_number) {
      newErrors.mobile_number = "Mobile number is required.";
    } else if (!/^\d{10}$/.test(formState.mobile_number)) {
      newErrors.mobile_number = "Mobile number must be 10 digits.";
    }

    if (!formState.thumb) newErrors.thumb = "Thumb image is required";

    if (formState.features.length > 0) {
      formState.features.forEach((feature, index) => {
        if (feature?.feature_name === "") {
          newErrors[`feature_name_${index}`] = "Feature name is required";
        }
        if (feature?.color === "") {
          newErrors[`color_${index}`] = "Feature color is required";
        }
      });
    } else {
      newErrors[`feature_name`] = "Feature name is required";
    }

    if (formState.price_chart?.price_chart === "") {
      newErrors.price_chart = "Price is required";
    }
    if (formState.price_chart?.text === "") {
      newErrors.text = "Price heading is required";
    }
    if (formState.price_chart?.details === "") {
      newErrors.details = "Price description is required";
    }
    if (formState.terms_and_condition?.terms_and_condition === "") {
      newErrors.terms_and_condition = "Terms and condition is required";
    }

    if (!formState.open_time) newErrors.open_time = "Select the open time";
    if (!formState.close_time) newErrors.close_time = "Select the close time";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const categoryNamesWithIds = categoryList.map((category) => ({
    id: category.id,
    name: category.category_name,
  }));

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "thumb" && files.length > 0) {
      setFormState({
        ...formState,
        thumb: files[0],
      });
    } else if (Object.keys(formState.price_chart).includes(e.target.name)) {
      setFormState({
        ...formState,
        price_chart: { ...formState.price_chart, [name]: value },
      });
    } else {
      setFormState({
        ...formState,
        [name]: value,
      });
    }
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleFeatureChange = (e, index) => {
    const updatedFeatures = formState.features.map((feature, i) => {
      if (i === index) {
        return {
          ...feature,
          [e.target.name]: e.target.value,
        };
      }
      return feature;
    });

    setFormState((prevState) => ({
      ...prevState,
      features: updatedFeatures,
    }));
    setErrors((prevErrors) => ({ ...prevErrors, [e.target.name]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validationForm()) {
      onSubmit(formState);
      onClose();
    }
  };

  const addFeature = (e) => {
    e.preventDefault();
    setFormState((prevState) => ({
      ...prevState,
      features: [...prevState.features, { feature_name: "", color: "" }],
    }));
  };

  const removeFeature = (idx) => {
    const values = [...formState.features];
    values.splice(idx, 1);
    setFormState({ ...formState, features: values });
  };

  function handleTimeChange(newValue, timeType) {
    const formattedTime = newValue.format("HH:mm");

    setFormState({ ...formState, [timeType]: formattedTime });
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle style={{ color: "#ff4d4d" }}>
        {service ? "Edit Service" : "Add Service"}
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Service"
          name="service"
          value={formState.service}
          onChange={handleChange}
          margin="dense"
          error={!!errors.service}
          helperText={errors.service}
        />
        <FormControl fullWidth margin="dense" error={!!errors.category}>
          <InputLabel>Category</InputLabel>
          <Select
            name="category"
            label="Category"
            value={formState.category}
            onChange={handleChange}
          >
            {categoryNamesWithIds.map((categoryName) => (
              <MenuItem key={categoryName.id} value={categoryName.id}>
                {categoryName.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          label="Description"
          name="description"
          value={formState.description}
          onChange={handleChange}
          margin="dense"
          multiline
          rows={4}
          error={!!errors.description}
          helperText={errors.description}
        />
         <TextField
          fullWidth
          label="Terms and Conditions"
          name="terms_and_condition"
          value={formState.terms_and_condition}
          onChange={handleChange}
          margin="dense"
          multiline
          rows={4}
          error={!!errors.terms_and_condition}
          helperText={errors.terms_and_condition}
        />
        <TextField
          fullWidth
          label="Mobile Number"
          name="mobile_number"
          value={formState.mobile_number}
          onChange={handleChange}
          margin="dense"
          error={!!errors.mobile_number}
          helperText={errors.mobile_number}
        />
        <label class="image_input_container" error={!!errors.thumb}>
          <img
            className="thumb-img"
            src={`${baseUrl}${formState.thumb}`}
            alt=""
          />
          <input
            type="file"
            name="thumb"
            onChange={handleChange}
            error={!!errors.thumb}
          />
        </label>
        <Typography gutterBottom className="mt-3">
          Price Details and Timing:
        </Typography>
        <TextField
          margin="dense"
          name="price_chart"
          value={formState.price_chart.price_chart}
          onChange={handleChange}
          label="Price"
          type="text"
          fullWidth
          variant="outlined"
          error={!!errors.price_chart}
          helperText={errors.price_chart}
        />
        <TextField
          margin="dense"
          name="text"
          value={formState.price_chart.text}
          onChange={handleChange}
          label="Price Heading"
          type="text"
          fullWidth
          variant="outlined"
          error={!!errors.text}
          helperText={errors.text}
        />
        <TextField
          margin="dense"
          name="details"
          value={formState.price_chart.details}
          onChange={handleChange}
          label="Price Description"
          type="text"
          fullWidth
          variant="outlined"
          multiline
          rows={4}
          error={!!errors.details}
          helperText={errors.details}
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <TimePicker
            label="Open Time"
            value={
              formState.open_time ? dayjs(formState.open_time, "HH:mm") : null
            }
            onChange={(newValue) => {
              if (newValue) {
                handleTimeChange(newValue, "open_time");
              } else {
                setErrors((prevErrors) => ({
                  ...prevErrors,
                  open_time: "Invalid time",
                }));
              }
            }}
            slotProps={{
              textField: {
                error: !!errors.open_time,
                helperText: errors.open_time,
                sx: { width: "100%", marginTop: "5px" },
              },
            }}
          />

          {/* Close Time Picker */}
          <TimePicker
            label="Close Time"
            value={
              formState.close_time ? dayjs(formState.close_time, "HH:mm") : null
            }
            onChange={(newValue) => {
              if (newValue) {
                handleTimeChange(newValue, "close_time");
              } else {
                setErrors((prevErrors) => ({
                  ...prevErrors,
                  close_time: "Invalid time",
                }));
              }
            }}
            slotProps={{
              textField: {
                error: !!errors.close_time,
                helperText: errors.close_time,
                sx: { width: "100%", marginTop: "7px" },
              },
            }}
          />
        </LocalizationProvider>

        <Typography variant="h6" gutterBottom>
          Add Features
        </Typography>

        {formState.features.map((feature, index) => {
          return (
            <div key={index} style={{ display: "flex", alignItems: "center" }}>
              <TextField
                margin="dense"
                placeholder="Enter feature"
                name="feature_name"
                value={feature.feature_name}
                fullWidth
                variant="outlined"
                onChange={(e) => handleFeatureChange(e, index)}
                error={!!errors[`feature_name_${index}`]}
                helperText={errors[`feature_name_${index}`]}
              />
              <FormControl className="">
                <FormLabel>Color</FormLabel>
                <input
                  type="color"
                  value={feature.color}
                  onChange={(e) => handleFeatureChange(e, index)}
                  name="color"
                  style={{
                    marginLeft: "10px",
                    height: "40px",
                    width: "40px",
                    border: "none",
                  }}
                />
                {errors[`color_${index}`] && (
                  <p color="error" className="error-text">
                    {errors[`color_${index}`]}
                  </p>
                )}
              </FormControl>
              <IconButton onClick={removeFeature}>
                <CloseIcon />
              </IconButton>
            </div>
          );
        })}

        <Button variant="contained" color="secondary" onClick={addFeature}>
          + Add Feature
        </Button>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant="contained"
          color="error"
          style={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="success"
          style={{ textTransform: "none" }}
        >
          {service ? "Edit" : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddServiceForm;
